// Interview session & answer server functions.
// - Admin-only fns: list/create/delete sessions, manage drafts, publish/reject.
// - Public token-scoped fns: getSessionByToken, saveAnswer, completeInterview.
//   These load supabaseAdmin inside the handler (server-only) and validate the
//   token themselves — anon has no direct table access.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { parseYouTubeId, youtubeThumbnailUrl, youtubeWatchUrl } from "@/lib/youtube";

export type InterviewQuestion = { id: string; question_text: string; order: number };

export type SessionStatus =
  | "draft"
  | "sent"
  | "in_progress"
  | "awaiting_ai_draft"
  | "pending_approval"
  | "published";

export type Credit = { name: string; role?: string | null; person_id?: string | null };

export type InterviewSession = {
  id: string;
  topic: string;
  target_keyword: string;
  category: string | null;
  questions: InterviewQuestion[];
  interview_token: string | null;
  status: SessionStatus;
  person_id: string | null;
  length_mode: string;
  attribution_mode: string;
  created_at: string;
  sent_at: string | null;
  completed_at: string | null;
  answered_count?: number;
};

export type InterviewAnswer = {
  id: string;
  session_id: string;
  question_id: string;
  question_text: string;
  audio_url: string | null;
  transcript: string;
  created_at: string;
};

export type DraftSection = { heading: string; body: string; quote?: string | null };

export type InterviewDraftPost = {
  id: string;
  session_id: string;
  title: string;
  slug: string;
  meta_title: string;
  meta_description: string;
  intro_paragraph: string;
  body_sections: DraftSection[];
  faq_items: Array<{ question: string; answer: string }>;
  author_name: string | null;
  authors: Credit[];
  contributors: Credit[];
  reviewers: Credit[];
  featured_image_id: string | null;
  category_id: string | null;
  youtube_url: string | null;
  video_thumbnail_url: string | null;
  status: "pending_approval" | "approved" | "published" | "rejected";
  created_at: string;
  approved_at: string | null;
};


async function ensureAdmin(supabase: any, userId: string) {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!data) throw new Error("Forbidden");
}

async function getAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin as any;
}

function genToken(): string {
  // 32-char url-safe token
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/* ============ ADMIN: SESSIONS ============ */

export const adminListSessions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { data: sessions, error } = await sb
      .from("interview_sessions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const { data: counts } = await sb
      .from("interview_answers")
      .select("session_id");
    const tally: Record<string, number> = {};
    for (const r of counts ?? []) tally[r.session_id] = (tally[r.session_id] ?? 0) + 1;
    return (sessions ?? []).map((s: any) => ({
      ...s,
      answered_count: tally[s.id] ?? 0,
    })) as InterviewSession[];
  });

export const adminCreateSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        topic: z.string().min(2).max(2000),
        guidance: z.string().max(2000).default(""),
        target_keyword: z.string().max(200).default(""),
        category: z.string().max(120).nullable().optional(),
        questions: z.array(z.string().min(3)).min(1).max(20),
        person_id: z.string().uuid().nullable().optional(),
        length_mode: z.enum(["quick", "standard", "custom"]).default("standard"),
        attribution_mode: z
          .enum([
            "decide_at_review",
            "respondent_author",
            "staff_author",
            "team_author",
            "multiple_authors",
          ])
          .default("decide_at_review"),
        save_as_draft: z.boolean().default(false),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const questions: InterviewQuestion[] = data.questions.map((q, i) => ({
      id: `q${i + 1}-${crypto.randomUUID().slice(0, 8)}`,
      question_text: q,
      order: i,
    }));
    const interview_token = genToken();
    const { data: row, error } = await sb
      .from("interview_sessions")
      .insert({
        topic: data.topic,
        guidance: data.guidance,
        target_keyword: data.target_keyword,
        category: data.category ?? null,
        questions,
        interview_token,
        person_id: data.person_id ?? null,
        length_mode: data.length_mode,
        attribution_mode: data.attribution_mode,
        status: data.save_as_draft ? "draft" : "sent",
        sent_at: data.save_as_draft ? null : new Date().toISOString(),
      })
      .select("id, interview_token")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id, interview_token: row.interview_token };
  });

/** Update a not-yet-answered session: questions, topic, respondent, or send it. */
export const adminUpdateSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        topic: z.string().min(2).max(2000).optional(),
        guidance: z.string().max(2000).optional(),
        target_keyword: z.string().max(200).optional(),
        category: z.string().max(120).nullable().optional(),
        questions: z.array(z.string().min(3)).min(1).max(20).optional(),
        person_id: z.string().uuid().nullable().optional(),
        length_mode: z.enum(["quick", "standard", "custom"]).optional(),
        attribution_mode: z.string().max(40).optional(),
        send: z.boolean().optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { id, questions, send, ...rest } = data;
    const patch: Record<string, unknown> = { ...rest };
    if (questions) {
      patch.questions = questions.map((q, i) => ({
        id: `q${i + 1}-${crypto.randomUUID().slice(0, 8)}`,
        question_text: q,
        order: i,
      }));
    }
    if (send) {
      patch.status = "sent";
      patch.sent_at = new Date().toISOString();
    }
    const { error } = await sb.from("interview_sessions").update(patch).eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });



export const adminDeleteSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { error } = await sb.from("interview_sessions").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminGetSessionFull = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { data: session, error } = await sb
      .from("interview_sessions")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!session) return null;
    const { data: answers } = await sb
      .from("interview_answers")
      .select("*")
      .eq("session_id", data.id)
      .order("created_at", { ascending: true });
    const { data: draft } = await sb
      .from("interview_draft_posts")
      .select("*")
      .eq("session_id", data.id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    return {
      session: session as InterviewSession,
      answers: (answers ?? []) as InterviewAnswer[],
      draft: (draft ?? null) as InterviewDraftPost | null,
    };
  });

/* ============ ADMIN: DRAFT EDITS ============ */

const FaqItem = z.object({ question: z.string(), answer: z.string() });
const SectionItem = z.object({
  heading: z.string(),
  body: z.string(),
  quote: z.string().nullable().optional(),
});
const CreditItem = z.object({
  name: z.string().max(200),
  role: z.string().max(200).nullable().optional(),
  person_id: z.string().uuid().nullable().optional(),
});

export const adminUpdateDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        title: z.string().optional(),
        slug: z.string().optional(),
        meta_title: z.string().optional(),
        meta_description: z.string().optional(),
        intro_paragraph: z.string().optional(),
        body_sections: z.array(SectionItem).optional(),
        faq_items: z.array(FaqItem).optional(),
        authors: z.array(CreditItem).optional(),
        contributors: z.array(CreditItem).optional(),
        reviewers: z.array(CreditItem).optional(),
        featured_image_id: z.string().uuid().nullable().optional(),
        category_id: z.string().nullable().optional(),
        youtube_url: z.string().nullable().optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { id, youtube_url, ...rest } = data;
    const patch: Record<string, unknown> = { ...rest };
    if (data.authors) {
      patch.author_name = data.authors[0]?.name ?? null;
    }
    if (youtube_url !== undefined) {
      if (youtube_url === null || youtube_url === "") {
        patch.youtube_url = null;
        patch.video_thumbnail_url = null;
      } else {
        const vid = parseYouTubeId(youtube_url);
        if (!vid) throw new Error("Invalid YouTube URL");
        patch.youtube_url = youtubeWatchUrl(vid);
        patch.video_thumbnail_url = youtubeThumbnailUrl(vid);
      }
    }
    const { error } = await sb.from("interview_draft_posts").update(patch).eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

function escapeHtml(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function paragraphs(text: string) {
  return escapeHtml(text)
    .split(/\n{2,}|\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${p}</p>`)
    .join("\n");
}

/** Narrative article HTML: intro + topic-based H2 sections with optional pull-quotes. */
export function buildNarrativeHtml(
  intro: string,
  sections: DraftSection[],
  attributionName?: string | null,
): string {
  const introHtml = intro ? paragraphs(intro) : "";
  const body = (sections ?? [])
    .map((s) => {
      const quote = s.quote
        ? `<blockquote><p>${escapeHtml(s.quote)}</p>${
            attributionName ? `<p><cite>— ${escapeHtml(attributionName)}</cite></p>` : ""
          }</blockquote>`
        : "";
      return `<h2>${escapeHtml(s.heading)}</h2>\n${paragraphs(s.body)}${quote ? `\n${quote}` : ""}`;
    })
    .join("\n\n");
  return `${introHtml}\n\n${body}`.trim();
}

export const adminPublishDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ draft_id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { data: draft, error: dErr } = await sb
      .from("interview_draft_posts")
      .select("*")
      .eq("id", data.draft_id)
      .maybeSingle();
    if (dErr || !draft) throw new Error("Draft not found");

    const sections = (draft.body_sections ?? []) as DraftSection[];
    const faqItems = (draft.faq_items ?? []) as Array<{ question: string; answer: string }>;
    const contributors = (draft.contributors ?? []) as Credit[];
    const authors = ((draft.authors ?? []) as Credit[]).filter((a) => a?.name);
    const reviewers = (draft.reviewers ?? []) as Credit[];
    const quoteName = contributors[0]?.name ?? authors[0]?.name ?? null;

    // Narrative body. Legacy drafts with no sections fall back to their FAQ Q/A.
    const bodyHtml = sections.length
      ? buildNarrativeHtml(draft.intro_paragraph ?? "", sections, quoteName)
      : `${draft.intro_paragraph ? `<p>${escapeHtml(draft.intro_paragraph)}</p>` : ""}\n\n${faqItems
          .map((f) => `<h2>${escapeHtml(f.question)}</h2>\n${paragraphs(f.answer)}`)
          .join("\n\n")}`.trim();

    const slug = (draft.slug || draft.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")).replace(
      /^-|-$/g,
      "",
    );

    const { data: post, error: pErr } = await sb
      .from("blog_posts")
      .insert({
        slug,
        url_prefix: "blog",
        title: draft.title,
        excerpt: draft.intro_paragraph?.slice(0, 300) || null,
        body_html: bodyHtml,
        meta_title: draft.meta_title || null,
        meta_description: draft.meta_description || null,
        featured_image_id: draft.featured_image_id || null,
        category_id: draft.category_id || null,
        youtube_url: draft.youtube_url || null,
        video_thumbnail_url: draft.video_thumbnail_url || null,
        status: "published",
        published_at: new Date().toISOString(),
        tags: [],
        source: "interview",
        author: draft.author_name || authors[0]?.name || "Durafoam Roofing Team",
        authors,
        contributors,
        reviewers,
        faq_items: sections.length ? faqItems : [],
      })
      .select("id, slug")
      .single();
    if (pErr) throw new Error(pErr.message);


    await sb
      .from("interview_draft_posts")
      .update({ status: "published", approved_at: new Date().toISOString() })
      .eq("id", draft.id);
    await sb
      .from("interview_sessions")
      .update({ status: "published", interview_token: null })
      .eq("id", draft.session_id);

    return { ok: true, post_id: post.id, slug: post.slug };
  });

export const adminRejectDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ draft_id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { error } = await sb
      .from("interview_draft_posts")
      .update({ status: "rejected" })
      .eq("id", data.draft_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* ============ PUBLIC (token-validated) ============ */

export const getSessionByToken = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) =>
    z.object({ token: z.string().min(8).max(200) }).parse(i),
  )
  .handler(async ({ data }) => {
    const sb = await getAdmin();
    const { data: session } = await sb
      .from("interview_sessions")
      .select("*")
      .eq("interview_token", data.token)
      .maybeSingle();
    if (!session) return null;
    if (session.status === "published") return null;
    const { data: answers } = await sb
      .from("interview_answers")
      .select("question_id, transcript")
      .eq("session_id", session.id);
    return {
      session: {
        id: session.id,
        topic: session.topic,
        questions: session.questions,
        status: session.status,
      },
      answers: (answers ?? []) as Array<{ question_id: string; transcript: string }>,
    };
  });

export const saveAnswer = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) =>
    z
      .object({
        token: z.string().min(8).max(200),
        question_id: z.string().min(1),
        question_text: z.string().min(1).max(2000),
        transcript: z.string().max(20000),
        audio_url: z.string().url().optional().nullable(),
      })
      .parse(i),
  )
  .handler(async ({ data }) => {
    const sb = await getAdmin();
    const { data: session } = await sb
      .from("interview_sessions")
      .select("id, status")
      .eq("interview_token", data.token)
      .maybeSingle();
    if (!session || session.status === "published") throw new Error("Invalid link");

    const { error } = await sb
      .from("interview_answers")
      .upsert(
        {
          session_id: session.id,
          question_id: data.question_id,
          question_text: data.question_text,
          transcript: data.transcript,
          audio_url: data.audio_url ?? null,
        },
        { onConflict: "session_id,question_id" },
      );
    if (error) throw new Error(error.message);

    if (session.status === "sent" || session.status === "draft") {
      await sb
        .from("interview_sessions")
        .update({ status: "in_progress" })
        .eq("id", session.id);
    }
    return { ok: true };
  });

export const completeInterview = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) =>
    z.object({ token: z.string().min(8).max(200) }).parse(i),
  )
  .handler(async ({ data }) => {
    const sb = await getAdmin();
    const { data: session } = await sb
      .from("interview_sessions")
      .select("id, topic, status")
      .eq("interview_token", data.token)
      .maybeSingle();
    if (!session || session.status === "published") throw new Error("Invalid link");

    await sb
      .from("interview_sessions")
      .update({
        status: "awaiting_ai_draft",
        completed_at: new Date().toISOString(),
      })
      .eq("id", session.id);

    // Fire-and-forget: AI draft + notifications
    try {
      await notifyInterviewCompleted(session.topic);
      const { generateInterviewDraftForSession } = await import(
        "@/lib/interview-draft.server"
      );
      const draftRes = await generateInterviewDraftForSession(session.id);
      if (draftRes.ok) {
        await notifyDraftReady(draftRes.title, session.id);
      } else {
        console.error("[interview] draft generation failed:", draftRes.error);
      }
    } catch (err) {
      console.error("[interview] post-complete pipeline failed", err);
    }
    return { ok: true };
  });

async function notifyInterviewCompleted(topic: string) {
  const { enqueueTransactionalEmail } = await import("@/lib/email/send-internal.server");
  await enqueueTransactionalEmail({
    templateName: "interview-completed",
    recipientEmail: "bforstie@gmail.com",
    idempotencyKey: `interview-complete-${topic}-${Date.now()}`,
    templateData: { topic },
  });
}

async function notifyDraftReady(title: string, session_id: string) {
  const { enqueueTransactionalEmail } = await import("@/lib/email/send-internal.server");
  await enqueueTransactionalEmail({
    templateName: "interview-draft-ready",
    recipientEmail: "bforstie@gmail.com",
    idempotencyKey: `interview-draft-${session_id}`,
    templateData: {
      title,
      reviewUrl: `https://www.durafoaminc.com/admin?module=interviews&session=${session_id}`,
    },
  });
}
